import { Component, DoCheck, EventEmitter, Input, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ApiRoutes } from '@sb/library/api';
import { BaseViewComponent, Environment, ErrorMessages, FormBase, IFrameScrollMessage, Optional, ProductName, Required, SubmissionBarKind, Validators, string } from '@sb/library/base';
import { FieldMode, FormAffinity, ParentSignupInputItem, PropertyLengths, StudentTypes, TagDisplayItem, TeacherNameDisplayItem, VerifyEmailInputItem, WebsiteBlockSignupFormDisplayItem, WebsiteRenderContextTeacherNameDisplayItem } from '@sb/library/data-models';
import { SBDate } from '@sb/library/localization';
import { StepperComponent } from '@sb/library/stepper';
import { forkJoin, of } from 'rxjs';
import { SignupFormCustomFieldsComponent } from '../custom-fields/signup-form-custom-fields.component';
import { CalendarBookingFrontendInputItem, SignupData, SignupSteps, SignupStudentMode, StudentSignupFrontendInputItem, StudentSignupType } from '../signup-form.definitions';

class AddStudentForm extends FormBase { // doesn't work if you put export class
    StudentType = Required(StudentSignupType);
    FirstName = Required(string);
    LastName = Required(string);
    Email = Required(string); // Only required if adult student
    Gender = Optional(string); // Optional Fields
    Birthday =  Optional(SBDate)
    LocalSchool =  Optional(string);
    SubjectTag = Optional(TagDisplayItem);
    SkillLevel = Optional(string);
    FaceTimeID = Optional(string);
    SkypeUsername = Optional(string);
    PreferredTeacherID = Optional(string);
    Address = Optional(string);
    Referrer = Optional(string);
    Telephone = Optional(string);
    Parent = {
        FirstName: Required(string),
        LastName: Required(string),
        Email: Required(string),
        MobilePhone:  Required(string),
    }
}

declare const window: Window & {
    parentIFrame: any;
};

@Component({
    selector: 'lib-signup-form-design-block-student',
    templateUrl: './signup-form-design-block-student.component.html',
    styleUrls: ['./signup-form-design-block-student.component.scss']
})
export class SignupFormDesignBlockStudentComponent extends BaseViewComponent implements OnInit, DoCheck {
    @ViewChild('studentCustomFields', {static: false}) studentCustomFields!: SignupFormCustomFieldsComponent;
    @ViewChild('formCustomFields', {static: false}) formCustomFields!: SignupFormCustomFieldsComponent;

    @Input()
    set data(value: WebsiteBlockSignupFormDisplayItem) {
        if (value !== undefined) {
            this._data = value;
        }
    }
    @Input() teachers!: WebsiteRenderContextTeacherNameDisplayItem[];

    private _subjectTags!: TagDisplayItem[];
    @Input()
    set subjectTags(value: TagDisplayItem[]) {
        this._subjectTags = value;
        this._preferredSubjectTags = this.subjectTags;
    }

    @Input() isViewOnly: boolean = false;
    @Input() signupStudentMode!: SignupStudentMode;
    @Input() isBookingWidget!: boolean;
    @Input() existingEvents: boolean = false;
    @Input() set bookingRequest(value: CalendarBookingFrontendInputItem | null) {
        if (value !== null) {
            setTimeout(() => { // This settimeout is here because there is a race-condition when we go to add the additional student - this guarantees all inputs are available
                this._bookingRequest = value;

                if (this.isBookingWidget && !value.SubjectTagID) {
                    this.PreferredTeacherChanged(this.bookingRequest!.TeacherID);
                }
            });
        }
    }
    @Input() stepper!: StepperComponent<typeof SignupSteps>;
    @Input() hasPaymentType!: boolean;

    @Output() signupClicked: EventEmitter<SignupData> = new EventEmitter<SignupData>();
    @Output() secondaryActionClicked: EventEmitter<void> = new EventEmitter();

    readonly NO_PREFERRED_SUBJECT: string = $localize`:@@SUFDesignBlockNoPreferredSubject:No Preferred #Subject#`;
    readonly NO_PREFERRED_TEACHER: string = $localize`:@@SUFDesignBlockNoPreferredTeacher:No Preferred #Teacher#`;
    private _data!: WebsiteBlockSignupFormDisplayItem;
    private _inputItem: SignupData = new SignupData();
    private _specialMinimalDate: SBDate = SBDate.FromComponents(1900, 1, 1);
    private _todaysDate: SBDate = SBDate.Today();
    private _preferredTeachers: WebsiteRenderContextTeacherNameDisplayItem[] = [];
    private _preferredSubjectTags: TagDisplayItem[] = [];
    private _verifyingStudent: boolean = false;
    private _hasStudentCustomFields: boolean = false;
    private _hasFormCustomFields: boolean = false;
    private _bookingRequest: CalendarBookingFrontendInputItem | null = null;

    private _submissionBarKind: SubmissionBarKind = SubmissionBarKind.WidgetBasic;
    private _lastAdditionalFields: (FieldMode | undefined)[] = [];

    constructor(
        private _Environment: Environment,
        private _NgZone: NgZone
    ) {
        super();
    }

    readonly addStudentForm = AddStudentForm.Create(false);

    readonly firstNameError: ErrorMessages = {
        required: _ => $localize`:@@SUFAddStudentFirstNameMissingError:Please enter a first name`
    }

    readonly lastNameError: ErrorMessages = {
        required: _ => $localize`:@@SUFAddStudentLastNameMissingError:Please enter a last name`
    }

    readonly emailError: ErrorMessages = {
        required: _ => $localize`:@@SUFAddStudentEmailMissingError:Please enter an email address`,
        api: _ => $localize`:@@SUFAddStudentVerifyEmailAPIError:Please enter a valid email address`
    }

    readonly phoneError: ErrorMessages = {
        required: _ => $localize`:@@SUFAddStudentPhoneRequiredError:Please enter a valid phone number`,
    }

    readonly addressError: ErrorMessages = {
        required: _ => $localize `:@@SUFAddressMissingError: Please enter an address`
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this._submissionBarKind = this.GetSubmissionBarKind();
        this.SetupForm();
        this._lastAdditionalFields = this.GetAdditionalFieldModes();
    }

    ngDoCheck(): void {
        //TODO: Marc because we mutate in place, we need to check each field individually rather than relying on object reference changes
        // data.AdditionalFields is mutated in place by the caller, so the data @Input setter never re-fires
        const current = this.GetAdditionalFieldModes();
        if (current.some((mode, i) => mode !== this._lastAdditionalFields[i])) {
            this._lastAdditionalFields = current;
            this.ApplyAdditionalFieldRequirements();
        }
    }

    private GetAdditionalFieldModes(): (FieldMode | undefined)[] {
        const fields = this.data.AdditionalFields;
        return [
            fields?.Gender, fields?.Birthday, fields?.LocalSchool, fields?.SubjectTag, fields?.SkillLevel,
            fields?.FaceTimeID, fields?.SkypeUsername, fields?.PreferredTeacher, fields?.Address,
            fields?.Referrer, fields?.ChildPhone, fields?.ChildEmail
        ];
    }

    private GetSubmissionBarKind(): SubmissionBarKind {
        if (this.isBookingWidget) {
            return SubmissionBarKind.WidgetStepper;
        } else {
            if (this.signupStudentMode === SignupStudentMode.Anonymous) {
                return SubmissionBarKind.WidgetBasic;
            } else {
                if (this.signupStudentMode === SignupStudentMode.AdditionalStudent && this.hasPaymentType ) {
                    return SubmissionBarKind.WidgetStepper;
                } else {
                    return SubmissionBarKind.WidgetBasic;
                }
            }
        }
    }

    private SetupForm(): void {

        if (this.data.AllowedStudentTypes === StudentTypes.ChildOnly ) {
            this.addStudentForm.controls.StudentType.setValue(StudentSignupType.ChildStudent);
        } else if (this.data.AllowedStudentTypes === StudentTypes.AdultOnly ) {
            this.addStudentForm.controls.StudentType.setValue(StudentSignupType.AdultStudent);
            this.addStudentForm.controls.Parent.Disable();
        } else {
            switch (this._Environment.Application.ProductName) {
                case ProductName.AthletaDesk:
                    this.addStudentForm.controls.StudentType.setValue(StudentSignupType.AdultStudent);
                    break;
                case ProductName.MyMusicStaff:
                case ProductName.TutorBird:
                case ProductName.Test443:
                    this.addStudentForm.controls.StudentType.setValue(StudentSignupType.ChildStudent);
                    break;
                default:
                    this.addStudentForm.controls.StudentType.setValue(StudentSignupType.ChildStudent);
                    break;
            }
        }

        this.StudentTypeChanged(this.addStudentForm.controls.StudentType.value);
        this._preferredTeachers = this.teachers;
        this._preferredSubjectTags = this.subjectTags;

        if (this.signupStudentMode === SignupStudentMode.AdditionalStudent) {
            this.addStudentForm.controls.Parent.Disable();
            this.addStudentForm.controls.Address.Disable();
            this.addStudentForm.controls.Referrer.Disable();
        }

        if (this.isBookingWidget) {
            this.addStudentForm.controls.PreferredTeacherID.Disable();
        }

        //Additional Fields

        this.ApplyAdditionalFieldRequirements();

        this.addStudentForm.controls.FirstName.AddValidators(Validators.maxLength(PropertyLengths.Student.FirstName));
        this.addStudentForm.controls.LastName.AddValidators(Validators.maxLength(PropertyLengths.Student.LastName));
        this.addStudentForm.controls.Email.AddValidators(Validators.maxLength(PropertyLengths.Student.EmailAddress));
        this.addStudentForm.controls.Gender.AddValidators(Validators.maxLength(PropertyLengths.Student.Gender));
        this.addStudentForm.controls.FaceTimeID.AddValidators(Validators.maxLength(PropertyLengths.Student.FaceTimeID));
        this.addStudentForm.controls.SkillLevel.AddValidators(Validators.maxLength(PropertyLengths.Student.Level));
        this.addStudentForm.controls.LocalSchool.AddValidators(Validators.maxLength(PropertyLengths.Student.LocalSchool));
        this.addStudentForm.controls.Telephone.AddValidators(Validators.maxLength(PropertyLengths.Student.MobilePhone));
        this.addStudentForm.controls.Referrer.AddValidators(Validators.maxLength(PropertyLengths.Student.Referrer));
        this.addStudentForm.controls.SkypeUsername.AddValidators(Validators.maxLength(PropertyLengths.Student.SkypeUsername));
        this.addStudentForm.controls.Address.AddValidators(Validators.maxLength(PropertyLengths.Parent.Address));
        
        this.addStudentForm.controls.Parent.controls.Email.AddValidators(Validators.maxLength(PropertyLengths.Parent.EmailAddress));
        this.addStudentForm.controls.Parent.controls.FirstName.AddValidators(Validators.maxLength(PropertyLengths.Parent.FirstName));
        this.addStudentForm.controls.Parent.controls.LastName.AddValidators(Validators.maxLength(PropertyLengths.Parent.LastName));
        this.addStudentForm.controls.Parent.controls.MobilePhone.AddValidators(Validators.maxLength(PropertyLengths.Parent.MobilePhone));

        this._hasStudentCustomFields = this.HasCustomFields(FormAffinity.Student);
        this._hasFormCustomFields = this.HasCustomFields(FormAffinity.Form) && this.signupStudentMode !== SignupStudentMode.AdditionalStudent;
        
        setTimeout(() => {
            this.addStudentForm.isViewOnly = this.isViewOnly;
        });

        this.addStudentForm.SetDefaultValueToCurrent();
    }

    SignupClicked(): void {

        // On iOS, initial click on the button (without interacting with a form field) starts outside the ngZone
        this._NgZone.run(() => {
            this.addStudentForm.CheckValidity({ScrollToFirstError: false}).subscribe(result => {
                if (result) {
                    const form = this.addStudentForm.controls;
                    const parentForm = this.addStudentForm.controls.Parent.controls;
                    this._inputItem = new SignupData({
                        Mode: this.signupStudentMode,
                        Student: new StudentSignupFrontendInputItem({
                            IsAdult: form.StudentType.value === StudentSignupType.AdultStudent,
                            FirstName: form.FirstName.value,
                            LastName: form.LastName.value,
                            EmailAddress: form.Email.value,
                            Birthday: form.Birthday.value ? form.Birthday.value.ToISO() : null,
                            Gender: form.Gender.value,
                            LocalSchool: form.LocalSchool.value,
                            SubjectTagID: this.GetSubjectTagID(),
                            SkillLevel: form.SkillLevel.value,
                            FaceTimeID: form.FaceTimeID.value,
                            SkypeUsername: form.SkypeUsername.value,
                            PreferredTeacherID: !form.PreferredTeacherID.value? null: form.PreferredTeacherID.value,
                            CustomFieldValues: (this.studentCustomFields?.GetCustomFieldValues() ?? []).concat(this.formCustomFields?.GetCustomFieldValues() ?? []),
                            TelephoneNumber: form.Telephone.value,
                            IsAdditionalStudent: this.signupStudentMode === SignupStudentMode.AdditionalStudent
                        }),
                        Parent: new ParentSignupInputItem({
                            FirstName: (form.StudentType.value === StudentSignupType.AdultStudent && this.signupStudentMode === SignupStudentMode.Anonymous)
                                        ? form.FirstName.value
                                        : parentForm.FirstName.value,
                            LastName: (form.StudentType.value === StudentSignupType.AdultStudent && this.signupStudentMode === SignupStudentMode.Anonymous)
                                        ? form.LastName.value
                                        : parentForm.LastName.value,
                            EmailAddress: (form.StudentType.value === StudentSignupType.AdultStudent && this.signupStudentMode === SignupStudentMode.Anonymous)
                                        ? form.Email.value
                                        : parentForm.Email.value,
                            TelephoneNumberMobile: (form.StudentType.value === StudentSignupType.AdultStudent && this.signupStudentMode === SignupStudentMode.Anonymous)
                                                    ? form.Telephone.value
                                                    : parentForm.MobilePhone.value,
                            Address: form.Address.value,
                            Referrer: form.Referrer.value,
                        })
                    })

                    const studentEmailVerifyApiCall = this.addStudentForm.controls.Email.value
                                                    ? ApiRoutes.Messaging.VerifyEmail.Call({
                                                        Body: new VerifyEmailInputItem({
                                                            EmailAddress: this.addStudentForm.controls.Email.value
                                                        })})
                                                    : of(null);

                    const parentEmailVerifyApiCall = (form.StudentType.value === StudentSignupType.ChildStudent
                                                        && this.signupStudentMode === SignupStudentMode.Anonymous
                                                        && this.addStudentForm.controls.Parent.controls.Email.value)
                                                    ? ApiRoutes.Messaging.VerifyEmail.Call({
                                                        Body: new VerifyEmailInputItem({
                                                            EmailAddress: this.addStudentForm.controls.Parent.controls.Email.value
                                                        })})
                                                    : of(null);

                    forkJoin({
                        StudentEmailVerify: studentEmailVerifyApiCall,
                        ParentEmailVerify: parentEmailVerifyApiCall
                    }).subscribe(data => {

                        if (data.StudentEmailVerify === false && data.ParentEmailVerify === false) {
                            this.addStudentForm.Enable();
                            this.addStudentForm.controls.Email.SetApiError();
                            this.addStudentForm.controls.Parent.controls.Email.SetApiError();
                        } else if (data.StudentEmailVerify === false) {
                            this.addStudentForm.Enable();
                            this.addStudentForm.controls.Email.SetApiError();
                        } else if (data.ParentEmailVerify === false) {
                            this.addStudentForm.Enable();
                            this.addStudentForm.controls.Parent.controls.Email.SetApiError();
                        } else {
                            this.signupClicked.emit(this._inputItem);
                        }

                    });
                } else {
                    setTimeout(() => {
                        var elements = document.getElementsByClassName('mat-mdc-form-field-error');
                        var formField = elements[0]?.parentElement?.parentElement?.parentElement;

                        if (formField) {
                            const rect = formField.getBoundingClientRect();
                            window.parentIFrame?.sendMessage(new IFrameScrollMessage({ OffsetTop: rect.top, OffsetBottom: rect.bottom }));
                        }
                    }, 400);
                }
            });
        });
    }

    StudentTypeChanged(studentType: StudentSignupType): void {
        if (studentType === StudentSignupType.AdultStudent) {
            // If it's an additional student thats an adult, we don't want the email and phone since the family account is already created
            const isRequired = this.signupStudentMode !== SignupStudentMode.AdditionalStudent;
            this.UpdateControlIsRequired(this.addStudentForm.controls.Email, isRequired);
            this.UpdateControlIsRequired(this.addStudentForm.controls.Telephone, isRequired);
            this.addStudentForm.controls.Parent.Disable();
        } else {
            if (this.data.AdditionalFields?.ChildEmail === FieldMode.NotPresent
                || this.data.AdditionalFields?.ChildEmail === FieldMode.Optional) {
                this.UpdateControlIsRequired(this.addStudentForm.controls.Email, false);
            }

            if (this.data.AdditionalFields?.ChildEmail === FieldMode.NotPresent) {
                this.addStudentForm.controls.Email.setValue("");
            }

            if (this.data.AdditionalFields?.ChildPhone === FieldMode.NotPresent
                || this.data.AdditionalFields?.ChildPhone === FieldMode.Optional) {
                this.UpdateControlIsRequired(this.addStudentForm.controls.Telephone, false);
            }

            if (this.data.AdditionalFields?.ChildPhone === FieldMode.NotPresent) {
                this.addStudentForm.controls.Telephone.setValue("");
            }

            if (this.signupStudentMode === SignupStudentMode.Anonymous) {
                this.addStudentForm.controls.Parent.Enable();
            }
        }
    }

    SubjectTagChanged(tag: TagDisplayItem | null): void {
        if (tag) {
            this._preferredTeachers = this.teachers.filter(teacher => tag.TeacherIDs.includes(teacher.ID!));
        } else {
            this._preferredTeachers = this.teachers;
        }
        if (this.data.AdditionalFields!.PreferredTeacher === FieldMode.Required && this._preferredTeachers.length) {
            this.UpdateControlIsRequired(this.addStudentForm.controls.PreferredTeacherID, true);
        } else {
            this.UpdateControlIsRequired(this.addStudentForm.controls.PreferredTeacherID, false);
        }
    }

    PreferredTeacherChanged(teacherID: string | null): void {
        if (teacherID) {
            this._preferredSubjectTags = this.subjectTags.filter(tag => tag.TeacherIDs.includes(teacherID));
        } else {
            this._preferredSubjectTags = this.subjectTags;
        }
        if (this.data.AdditionalFields!.SubjectTag === FieldMode.Required && this._preferredSubjectTags.length) {
            this.UpdateControlIsRequired(this.addStudentForm.controls.SubjectTag, true);
        } else {
            this.UpdateControlIsRequired(this.addStudentForm.controls.SubjectTag, false);
        }
    }

    EnableForm(): void {
        this._verifyingStudent = false;
        this.addStudentForm.Enable();
    }

    Reset(): void {
        this._verifyingStudent = false;
        this._hasStudentCustomFields = false;
        this._hasFormCustomFields = false;
        this.addStudentForm.Reset();
        this._submissionBarKind = this.GetSubmissionBarKind();
        setTimeout(() => this.SetupForm());
    }

    StudentSecondaryActionClicked(): void {
        if (!this.isBookingWidget) {
            this.addStudentForm.Reset();
        }
        this.secondaryActionClicked.emit();
    }

    HasCustomFields(affinity: FormAffinity): boolean {
        return this.data.CustomFields.filter(customField => customField.Affinity === affinity).length > 0;
    }

    private UpdateControlIsRequired<T>(control: FormControl<T>, required: boolean): void {
        if (required) {
			control.AddValidators(Validators.required);
		} else {
			control.RemoveValidators(Validators.required);
		}
    }

    private ApplyAdditionalFieldRequirements(): void {
        this.UpdateControlIsRequired(this.addStudentForm.controls.Gender, this.data.AdditionalFields?.Gender === FieldMode.Required);
        this.UpdateControlIsRequired(this.addStudentForm.controls.Birthday, this.data.AdditionalFields?.Birthday === FieldMode.Required);
        this.UpdateControlIsRequired(this.addStudentForm.controls.LocalSchool, this.data.AdditionalFields?.LocalSchool === FieldMode.Required);
        this.UpdateControlIsRequired(this.addStudentForm.controls.SubjectTag, this.data.AdditionalFields?.SubjectTag === FieldMode.Required && this._preferredSubjectTags.length > 0);
        this.UpdateControlIsRequired(this.addStudentForm.controls.SkillLevel, this.data.AdditionalFields?.SkillLevel === FieldMode.Required);
        this.UpdateControlIsRequired(this.addStudentForm.controls.FaceTimeID, this.data.AdditionalFields?.FaceTimeID === FieldMode.Required);
        this.UpdateControlIsRequired(this.addStudentForm.controls.SkypeUsername, this.data.AdditionalFields?.SkypeUsername === FieldMode.Required);
        this.UpdateControlIsRequired(this.addStudentForm.controls.PreferredTeacherID, this.data.AdditionalFields?.PreferredTeacher === FieldMode.Required && this._preferredTeachers.length > 0);
        this.UpdateControlIsRequired(this.addStudentForm.controls.Address, this.data.AdditionalFields?.Address === FieldMode.Required);
        this.UpdateControlIsRequired(this.addStudentForm.controls.Referrer, this.data.AdditionalFields?.Referrer === FieldMode.Required);
        this.UpdateControlIsRequired(this.addStudentForm.controls.Telephone, this.data.AdditionalFields?.ChildPhone === FieldMode.Required);
        this.UpdateControlIsRequired(this.addStudentForm.controls.Email, this.data.AdditionalFields?.ChildEmail === FieldMode.Required);
    }

    private GetSubjectTagID(): string | null {
        if (this.isBookingWidget && this.bookingRequest && !this.existingEvents) { // booking is any subject and teacher subjects only 1.
            return this.bookingRequest.SubjectTagID;
        } else {
            return this.addStudentForm.controls.SubjectTag.value?.ID ?? null;
        }
    }

    get specialMinimalDate(): SBDate {
        return this._specialMinimalDate;
    }

    get todaysDate(): SBDate {
        return this._todaysDate;
    }

    get StudentSignupType(): typeof StudentSignupType {
        return StudentSignupType;
    }

    get StudentTypes(): typeof StudentTypes {
        return StudentTypes;
    }

    get FormAffinity(): typeof FormAffinity {
        return FormAffinity;
    }

    get preferredTeachers(): WebsiteRenderContextTeacherNameDisplayItem[] {
        return this._preferredTeachers;
    }

    get SignupStudentMode(): typeof SignupStudentMode {
        return SignupStudentMode;
    }

    get verifyingStudent(): boolean {
        return this._verifyingStudent;
    }

    get data() {
        return this._data;
    }

    get submissionBarKind(): SubmissionBarKind {
        return this._submissionBarKind;
    }
    get FieldMode(): typeof FieldMode {
        return FieldMode;
    }

    get preferredSubjectTags(): TagDisplayItem[] {
        return this._preferredSubjectTags;
    }

    get hasStudentCustomFields(): boolean {
        return this._hasStudentCustomFields;
    }
    get hasFormCustomFields(): boolean {
        return this._hasFormCustomFields;
    }

    get bookingRequest(): CalendarBookingFrontendInputItem | null {
        return this._bookingRequest
    }

    get currentStepIndex(): number {
        return this.stepper.currentStepPosition + 1;
    }

    get totalStepCounts(): number {
        return this.stepper.totalSteps - 1;
    }

    get subjectTags(): TagDisplayItem[] {
        return this._subjectTags;
    }
}
